import React, { useState, useEffect, useRef, useCallback } from 'react';
import { isIOS } from '../../services/appVersion.mjs';
import { colors, FONT, radii } from '../../theme';
import { X, Download, FileText, Edit3 } from 'lucide-react';
import { imageCache } from '../../services/imageCache';
import { fetchFileObjectURL } from '../../services/fileFetch';

export default function MediaViewer({ media, onClose, onEdit }) {
  const [blobUrl, setBlobUrl] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const urlRef = useRef(null);
  const fromCacheRef = useRef(false);

  // Zoom state
  const [scale, setScale] = useState(1);
  const [translate, setTranslate] = useState({ x: 0, y: 0 });
  const pinchRef = useRef({ startDist: 0, startScale: 1 });
  const panRef = useRef({ startX: 0, startY: 0, startTx: 0, startTy: 0, panning: false });

  const { fileId, fileName, mimeType, type, fileKey } = media;
  const isImage = type === 'image' || mimeType?.startsWith('image/');
  const isPDF = mimeType === 'application/pdf' || fileName?.toLowerCase().endsWith('.pdf');

  // Delegate to native iOS viewer — but never for a sealed file: the URL
  // names ciphertext the native side has no key for (§7в п.5). Those render
  // right here, where the key is.
  useEffect(() => {
    if (!fileKey && isIOS() && window.VoxioxNative?.openMediaViewer) {
      window.VoxioxNative.openMediaViewer(fileId, fileName, mimeType, `/api/files/${fileId}`);
      onClose();
    }
  }, [fileId]); // eslint-disable-line

  // Fetch file
  useEffect(() => {
    // For images, check cache first
    if (isImage) {
      const cached = imageCache.get(fileId);
      if (cached) {
        urlRef.current = cached;
        fromCacheRef.current = true;
        setBlobUrl(cached);
        setLoading(false);
        return;
      }
    }

    fetchFileObjectURL(fileId, fileKey, mimeType)
      .then(url => {
        urlRef.current = url;
        fromCacheRef.current = false;
        setBlobUrl(url);
        setLoading(false);
        if (isImage) imageCache.set(fileId, url);
      })
      .catch(err => {
        console.warn('[MediaViewer] load failed:', err.message);
        setError(err.message);
        setLoading(false);
      });

    return () => {
      if (urlRef.current && !fromCacheRef.current && !imageCache.has(fileId)) {
        URL.revokeObjectURL(urlRef.current);
      }
    };
  }, [fileId, isImage]);

  // Prevent body scroll
  useEffect(() => {
    document.body.style.overflow = 'hidden';
    return () => { document.body.style.overflow = ''; };
  }, []);

  // Reset zoom when media changes
  useEffect(() => { setScale(1); setTranslate({ x: 0, y: 0 }); }, [fileId]);

  const handleDownload = useCallback(() => {
    if (!blobUrl) return;
    const a = document.createElement('a');
    a.href = blobUrl;
    a.download = fileName || 'file';
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
  }, [blobUrl, fileName]);

  // Close on Escape
  useEffect(() => {
    const onKey = (e) => { if (e.key === 'Escape') onClose(); };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [onClose]);

  // ─── Pinch-to-zoom (touch) ───────────────────────────────
  const getTouchDist = (touches) => {
    const dx = touches[0].clientX - touches[1].clientX;
    const dy = touches[0].clientY - touches[1].clientY;
    return Math.hypot(dx, dy);
  };

  const onTouchStart = useCallback((e) => {
    if (e.touches.length === 2) {
      e.preventDefault();
      pinchRef.current = { startDist: getTouchDist(e.touches), startScale: scale };
    } else if (e.touches.length === 1 && scale > 1) {
      panRef.current = {
        startX: e.touches[0].clientX, startY: e.touches[0].clientY,
        startTx: translate.x, startTy: translate.y, panning: true,
      };
    }
  }, [scale, translate]);

  const onTouchMove = useCallback((e) => {
    if (e.touches.length === 2) {
      e.preventDefault();
      const dist = getTouchDist(e.touches);
      const newScale = Math.min(5, Math.max(1, pinchRef.current.startScale * (dist / pinchRef.current.startDist)));
      setScale(newScale);
      if (newScale <= 1) setTranslate({ x: 0, y: 0 });
    } else if (e.touches.length === 1 && panRef.current.panning && scale > 1) {
      const dx = e.touches[0].clientX - panRef.current.startX;
      const dy = e.touches[0].clientY - panRef.current.startY;
      setTranslate({ x: panRef.current.startTx + dx, y: panRef.current.startTy + dy });
    }
  }, [scale]);

  const onTouchEnd = useCallback(() => {
    panRef.current.panning = false;
    if (scale <= 1) { setScale(1); setTranslate({ x: 0, y: 0 }); }
  }, [scale]);

  // ─── Double-tap to toggle zoom ───────────────────────────
  const lastTapRef = useRef(0);
  const onDoubleTap = useCallback((e) => {
    const now = Date.now();
    if (now - lastTapRef.current < 300) {
      e.preventDefault();
      if (scale > 1) {
        setScale(1);
        setTranslate({ x: 0, y: 0 });
      } else {
        setScale(2.5);
      }
    }
    lastTapRef.current = now;
  }, [scale]);

  // ─── Wheel zoom (desktop) ────────────────────────────────
  const onWheel = useCallback((e) => {
    if (!isImage) return;
    e.preventDefault();
    const delta = e.deltaY > 0 ? -0.3 : 0.3;
    setScale(s => {
      const ns = Math.min(5, Math.max(1, s + delta));
      if (ns <= 1) setTranslate({ x: 0, y: 0 });
      return ns;
    });
  }, [isImage]);

  const handleOverlayClick = (e) => {
    if (e.target === e.currentTarget) onClose();
  };

  const handleEdit = () => {
    if (blobUrl && onEdit) onEdit(fileId, blobUrl);
  };

  return (
    <div style={S.overlay} onClick={handleOverlayClick}>
      {/* Top bar */}
      <div style={S.topBar}>
        <button onClick={onClose} style={S.iconBtn} aria-label="Закрыть">
          <X size={22} color={colors.white} />
        </button>
        <div style={S.title}>{fileName || (isImage ? 'Фото' : 'Документ')}</div>
        <button onClick={handleDownload} style={S.iconBtn} disabled={!blobUrl} aria-label="Скачать">
          <Download size={20} color={blobUrl ? colors.white : 'rgba(255,255,255,0.3)'} />
        </button>
      </div>

      {/* Content */}
      <div style={S.content} onWheel={onWheel}>
        {loading && (
          <div style={S.center}>
            <div style={S.spinner} />
            <div style={S.subText}>Загрузка...</div>
          </div>
        )}

        {error && (
          <div style={S.center}>
            <FileText size={48} color={colors.gray400} />
            <div style={S.subText}>Не удалось загрузить</div>
            <div style={{ fontSize: 13, color: colors.red, fontFamily: FONT, opacity: 0.8 }}>{error}</div>
          </div>
        )}

        {!loading && !error && blobUrl && (
          <>
            {isImage ? (
              <div
                style={S.imageWrap}
                onTouchStart={onTouchStart}
                onTouchMove={onTouchMove}
                onTouchEnd={onTouchEnd}
                onClick={onDoubleTap}
              >
                <img
                  src={blobUrl}
                  alt={fileName || 'Фото'}
                  draggable={false}
                  style={{
                    maxWidth: '94vw', maxHeight: '82vh',
                    objectFit: 'contain', userSelect: 'none', WebkitUserSelect: 'none',
                    transform: `scale(${scale}) translate(${translate.x / scale}px, ${translate.y / scale}px)`,
                    transition: scale === 1 ? 'transform 0.2s ease' : 'none',
                    willChange: 'transform',
                  }}
                />
              </div>
            ) : isPDF ? (
              <iframe
                src={blobUrl + '#toolbar=1'}
                title={fileName}
                style={S.iframe}
              />
            ) : (
              <div style={S.center}>
                <FileText size={64} color={colors.gray400} />
                <div style={{ fontSize: 17, fontWeight: 600, color: colors.white, fontFamily: FONT, textAlign: 'center', maxWidth: '80vw', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                  {fileName || 'Файл'}
                </div>
                <div style={S.subText}>Предпросмотр недоступен</div>
                <button onClick={handleDownload} style={S.bigDownloadBtn}>
                  <Download size={18} color={colors.white} />
                  <span>Скачать файл</span>
                </button>
              </div>
            )}
          </>
        )}
      </div>

      {/* Bottom bar (edit for images) */}
      {isImage && blobUrl && !loading && !error && (
        <div style={S.bottomBar}>
          <button onClick={handleEdit} style={S.editBtn}>
            <Edit3 size={20} color={colors.white} />
            <span style={{ fontSize: 14, color: colors.white, fontFamily: FONT }}>Редактировать</span>
          </button>
        </div>
      )}
    </div>
  );
}

// ─── Styles ────────────────────────────────────────────────
const S = {
  overlay: {
    position: 'fixed', inset: 0, zIndex: 300,
    background: 'rgba(0,0,0,0.95)',
    display: 'flex', flexDirection: 'column',
    fontFamily: FONT,
  },
  topBar: {
    display: 'flex', alignItems: 'center', gap: 12,
    padding: '12px 12px',
    paddingTop: 'max(12px, env(safe-area-inset-top))',
    background: 'rgba(17,24,39,0.95)',
    borderBottom: '1px solid rgba(255,255,255,0.1)',
    flexShrink: 0,
  },
  iconBtn: {
    width: 40, height: 40, borderRadius: '50%',
    background: 'rgba(255,255,255,0.15)',
    border: 'none', display: 'flex', alignItems: 'center', justifyContent: 'center',
    cursor: 'pointer', flexShrink: 0,
    WebkitTapHighlightColor: 'transparent',
  },
  title: {
    flex: 1, fontSize: 15, fontWeight: 500, color: colors.white,
    overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
    fontFamily: FONT,
  },
  content: {
    flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center',
    overflow: 'hidden', touchAction: 'none',
  },
  imageWrap: {
    width: '100%', height: '100%',
    display: 'flex', alignItems: 'center', justifyContent: 'center',
    overflow: 'hidden', touchAction: 'none',
  },
  iframe: {
    width: '100%', height: '100%', border: 'none',
    background: colors.white,
  },
  center: {
    display: 'flex', flexDirection: 'column', alignItems: 'center',
    justifyContent: 'center', gap: 12, padding: 40,
  },
  subText: {
    fontSize: 15, color: colors.gray500, fontFamily: FONT,
  },
  spinner: {
    width: 32, height: 32, borderRadius: '50%',
    border: '3px solid rgba(255,255,255,0.1)',
    borderTopColor: colors.avatarBlue,
    animation: 'spin 0.8s linear infinite',
  },
  bigDownloadBtn: {
    display: 'flex', alignItems: 'center', gap: 8,
    padding: '12px 24px', borderRadius: radii.md,
    background: colors.avatarBlue, border: 'none',
    color: colors.white, fontSize: 15, fontWeight: 500, fontFamily: FONT,
    cursor: 'pointer', marginTop: 8,
    WebkitTapHighlightColor: 'transparent',
  },
  bottomBar: {
    display: 'flex', alignItems: 'center', justifyContent: 'center',
    padding: '12px 16px',
    paddingBottom: 'max(12px, env(safe-area-inset-bottom))',
    background: 'rgba(17,24,39,0.95)',
    borderTop: '1px solid rgba(255,255,255,0.1)',
    flexShrink: 0,
  },
  editBtn: {
    display: 'flex', alignItems: 'center', gap: 8,
    padding: '10px 24px', borderRadius: radii.full,
    background: 'rgba(255,255,255,0.15)', border: 'none',
    cursor: 'pointer',
    WebkitTapHighlightColor: 'transparent',
  },
};
